import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { dialog } from "../Common/TreasuryDialog";
import { useHistory } from "react-router-dom";
import { createProperty, blankProperty } from "../../redux/actions/propertyAction";
import RiyalSymbol from "../RiyalSymbol";
import DraggableMap from "../Common/DraggableMap";

const GOOGLE_MAPS_EMBED_KEY = "AIzaSyB1oST8BuCKK4jfZ_Pev5XxBC6_VK1i7Xw";
const SECTORS = ["Residential", "Hospitality", "Commercial", "Industrial"];

const CreateProperty = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const searchTimeoutRef = useRef(null);
  const submittingRef = useRef(false); // guard: only show success dialog after a real submit
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mapQuery, setMapQuery] = useState("Riyadh, Saudi Arabia");
  const [mapCoords, setMapCoords] = useState({ lat: 24.7136, lng: 46.6753 });

  const { property, auth } = useSelector((state) => state);
  const [images, setImages] = useState({ thumbnails: {}, files: [] });
  const [deedname, setDeedName] = useState("");
  const [deed, setDeed] = useState("");
  const [docs, setDocs] = useState([]);

  const initialState = {
    userId: auth.data ? auth.data.user._id : "",
    title: "",
    address: "",
    city: "",
    sector: "",
    shortAddress: "",
    description: "",
    descriptionAr: "",
    imageName: [],
    propertyEstimatedValue: "",
    generatic_income: "",
    propertyDeed: "",
    percentageOfOwnership: "",
  };
  const [propertyData, setPropertyData] = useState(initialState);
  const { title, address, city, sector, shortAddress, description, descriptionAr, imageName, propertyEstimatedValue, generatic_income, propertyDeed, percentageOfOwnership } = propertyData;

  const errors = property.data && property.data.errors && typeof property.data.errors === "object" ? property.data.errors : {};

  // Address search using OpenStreetMap Nominatim (no API key needed)
  const searchAddress = (query) => {
    if (!query || query.length < 3) { setSuggestions([]); return; }
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=sa&limit=5&addressdetails=1`,
          { headers: { "Accept-Language": "en" } }
        );
        const data = await res.json();
        setSuggestions(data);
        setShowSuggestions(true);
        // Auto-move map pin to top result while user types
        if (data && data[0]) {
          const topLat = parseFloat(data[0].lat);
          const topLng = parseFloat(data[0].lon);
          if (!isNaN(topLat) && !isNaN(topLng)) {
            setMapCoords({ lat: topLat, lng: topLng });
          }
        }
      } catch (e) { setSuggestions([]); }
    }, 400);
  };

  const selectSuggestion = (item) => {
    const addr = item.display_name;
    // Prefer city > town > municipality > village — avoid county/suburb/district which are sub-city areas
    const city = item.address.city || item.address.town || item.address.municipality || item.address.village || item.address.state || "";
    const q = encodeURIComponent(addr);
    setMapQuery(q);
    // Update map pin to the selected location
    const newLat = parseFloat(item.lat);
    const newLng = parseFloat(item.lon);
    if (!isNaN(newLat) && !isNaN(newLng)) {
      setMapCoords({ lat: newLat, lng: newLng });
    }
    setPropertyData((prev) => ({ ...prev, address: addr, city }));
    setSuggestions([]);
    setShowSuggestions(false);
  };

  useEffect(() => {
    const status = property?.data?.status ?? property?.data?.data?.status;
    if ((status === 1 || status === "1") && submittingRef.current) {
      submittingRef.current = false;
      dialog.success("Property Submitted", "Your property listing has been received and is under review. Our team will contact you shortly.").then(() => {
        dispatch(blankProperty());
        history.push("/");
      });
    }
  }, [property?.data?.status, property?.data?.data?.status]);

  const handleChangeInput = (e) => {
    const { name, value } = e.target;
    setPropertyData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeFile = (file, set) => {
    if (set === "images") {
      if (!file || file.length === 0) return;
      const alreadyNames = images.files.map((f) => f.name);
      const unique = [];
      for (let i = 0; i < file.length; i++) {
        if (!alreadyNames.includes(file[i].name)) {
          if (file[i].size > 1 * 1024 * 1024) { dialog.error("File Too Large", "Image size must not exceed 1MB"); return; }
          unique.push(file[i]);
        }
      }
      const newThumbs = {};
      unique.forEach((f) => { newThumbs[f.name] = window.URL.createObjectURL(f); });
      setImages((prev) => ({ thumbnails: { ...prev.thumbnails, ...newThumbs }, files: [...prev.files, ...unique] }));
      setPropertyData((prev) => ({ ...prev, imageName: [...prev.imageName, ...unique.map((f) => f.name)] }));
    } else {
      setDeedName(file.name);
      setDeed(file);
      setPropertyData((prev) => ({ ...prev, propertyDeed: file.name }));
    }
  };

  const removeImage = (name) => {
    setImages((prev) => {
      window.URL.revokeObjectURL(prev.thumbnails[name]);
      const { [name]: _, ...rest } = prev.thumbnails;
      return { thumbnails: rest, files: prev.files.filter((f) => f.name !== name) };
    });
    setPropertyData((prev) => ({ ...prev, imageName: prev.imageName.filter((n) => n !== name) }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dialog.confirm("Submit Property for Review?", "Once submitted, our team will review your listing and contact you within 2 business days. You will not be able to edit the listing during review.").then((confirmed) => {
      if (confirmed) {
        submittingRef.current = true;
        const primaryDeed = deed || (docs.length > 0 ? docs[0] : null);
        dispatch(createProperty(propertyData, images.files, primaryDeed));
      }
    });
  };

  const isIncome = generatic_income === "1";

  return (
    <div style={{ minHeight: "100vh", background: "#f5f4f0", paddingTop: "40px", paddingBottom: "60px" }}>
      <div style={{ maxWidth: "860px", margin: "0 auto", padding: "0 20px" }}>

        {/* Page Header */}
        <h1 style={{ fontSize: "28px", fontWeight: "700", color: "#0e3725", margin: "0 0 6px", letterSpacing: "-0.3px" }}>List a Property</h1>
        <p style={{ fontSize: "14px", color: "#7a7a6e", margin: "0 0 32px" }}>Complete all sections to submit your property for tokenization review.</p>

        <form onSubmit={handleSubmit}>

          {/* ── Basic Information ── */}
          <div style={{ background: "#fff", borderRadius: "16px", padding: "32px", marginBottom: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)" }}>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#0e3725", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "20px", paddingBottom: "10px", borderBottom: "1px solid #e8e6e0", display: "flex", alignItems: "center", gap: "8px" }}>
              <svg style={{ width: "18px", height: "18px", color: "#c9a84c" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M3 9h18M9 21V9"/></svg>
              Basic Information
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#4a4a42", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.4px" }}>Property Title</label>
                <input style={{ width: "100%", padding: "11px 14px", border: "1.5px solid #e2e0d8", borderRadius: "10px", fontSize: "14px", color: "#1a1a14", background: "#fafaf8", outline: "none", boxSizing: "border-box" }} type="text" name="title" value={title} placeholder="e.g. Al Olaya Mixed-Use Tower" onChange={handleChangeInput} onFocus={e => (e.target.style.borderColor = "#0e3725")} onBlur={e => (e.target.style.borderColor = "#e2e0d8")} />
                {errors.title && <span style={{ fontSize: "11px", color: "#e05252", marginTop: "4px", display: "block" }}>{errors.title}</span>}
              </div>
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#4a4a42", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.4px" }}>Description — Arabic <span style={{ color: "#e05252" }}>*</span></label>
                <textarea
                  style={{ width: "100%", padding: "11px 14px", border: "1.5px solid #e2e0d8", borderRadius: "10px", fontSize: "14px", color: "#1a1a14", background: "#fafaf8", outline: "none", resize: "vertical", minHeight: "100px", boxSizing: "border-box", direction: "rtl", fontFamily: "'Tajawal', sans-serif" }}
                  name="descriptionAr"
                  value={descriptionAr}
                  placeholder="صف العقار ومميزاته وفرص الاستثمار..."
                  onChange={handleChangeInput}
                  onFocus={e => (e.target.style.borderColor = "#0e3725")}
                  onBlur={e => (e.target.style.borderColor = "#e2e0d8")}
                  required
                />
                {errors.descriptionAr && <span style={{ fontSize: "11px", color: "#e05252", marginTop: "4px", display: "block" }}>{errors.descriptionAr}</span>}
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#4a4a42", textTransform: "uppercase", letterSpacing: "0.4px" }}>Description — English</label>
                  <button
                    type="button"
                    style={{ fontSize: "11px", color: "#0e3725", background: "none", border: "1px solid #0e3725", borderRadius: "6px", padding: "3px 10px", cursor: "pointer", fontWeight: "600" }}
                    onClick={async () => {
                      if (!descriptionAr) return;
                      try {
                        const res = await fetch(`https://api.mymemory.translated.net/get?q=${encodeURIComponent(descriptionAr)}&langpair=ar|en`);
                        const data = await res.json();
                        if (data.responseStatus === 200) {
                          setPropertyData(prev => ({ ...prev, description: data.responseData.translatedText }));
                        }
                      } catch (e) { /* silent fail */ }
                    }}
                  >
                    ⇆ Auto-translate from Arabic
                  </button>
                </div>
                <textarea style={{ width: "100%", padding: "11px 14px", border: "1.5px solid #e2e0d8", borderRadius: "10px", fontSize: "14px", color: "#1a1a14", background: "#fafaf8", outline: "none", resize: "vertical", minHeight: "100px", boxSizing: "border-box" }} name="description" value={description} placeholder="Describe the property, its features, and investment highlights..." onChange={handleChangeInput} onFocus={e => (e.target.style.borderColor = "#0e3725")} onBlur={e => (e.target.style.borderColor = "#e2e0d8")} />
                {errors.description && <span style={{ fontSize: "11px", color: "#e05252", marginTop: "4px", display: "block" }}>{errors.description}</span>}
              </div>
            </div>
          </div>

          {/* ── Location ── */}
          <div style={{ background: "#fff", borderRadius: "16px", padding: "32px", marginBottom: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)" }}>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#0e3725", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "20px", paddingBottom: "10px", borderBottom: "1px solid #e8e6e0", display: "flex", alignItems: "center", gap: "8px" }}>
              <svg style={{ width: "18px", height: "18px", color: "#c9a84c" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
              Location
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

              {/* Row: Short Address (left, compact) + Full Address (right) */}
              <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>

                {/* Short Address - compact left column */}
                <div style={{ flex: "0 0 auto", width: "170px" }}>
                  <label style={{ display: "block", fontSize: "11px", fontWeight: "700", color: "#4a4a42", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Short Address
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      style={{ width: "100%", padding: "11px 30px 11px 12px", border: "1.5px solid #e2e0d8", borderRadius: "10px", fontSize: "13px", color: "#0e3725", background: "#fafaf8", outline: "none", boxSizing: "border-box", textTransform: "uppercase", letterSpacing: "1.5px", fontWeight: "400", textAlign: "center" }}
                      type="text"
                      name="shortAddress"
                      value={shortAddress}
                      placeholder="e.g. RUHC2087"
                      maxLength={8}
                      onChange={async (e) => {
                        const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
                        setPropertyData((prev) => ({ ...prev, shortAddress: val }));
                        if (val.length === 8) {
                          // Saudi short address city code map (first 4 chars)
                          const CITY_CODES = {
                            RUHC: "Riyadh", RUHB: "Riyadh", RUHA: "Riyadh", RUHD: "Riyadh",
                            JDCA: "Jeddah", JDCB: "Jeddah", JDCC: "Jeddah", JDCD: "Jeddah",
                            MECA: "Mecca", MECC: "Mecca", MEDA: "Medina", MEDB: "Medina",
                            DMMA: "Dammam", DMMB: "Dammam", KFUA: "Khobar", DHHA: "Dhahran",
                            ABHA: "Abha", TAIA: "Taif", BURA: "Buraydah", HAIA: "Hail",
                            TABA: "Tabuk", ARAA: "Arar", SKAA: "Sakaka", JIZA: "Jizan",
                            NAJA: "Najran", BAHA: "Baha", YANB: "Yanbu",
                          };
                          const prefix = val.substring(0, 4).toUpperCase();
                          const cityFromCode = CITY_CODES[prefix] || "";
                          // Update map query with short address
                          setMapQuery(encodeURIComponent(val + ", Saudi Arabia"));
                          // Auto-fill city from code map immediately
                          if (cityFromCode) {
                            setPropertyData((prev) => ({ ...prev, shortAddress: val, city: cityFromCode }));
                          }
                          // Use Google Geocoding API for precise short address lookup
                          try {
                            const geoRes = await fetch(
                              `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(val)}&region=sa&key=AIzaSyB1oST8BuCKK4jfZ_Pev5XxBC6_VK1i7Xw`
                            );
                            const geoData = await geoRes.json();
                            if (geoData.status === "OK" && geoData.results && geoData.results[0]) {
                              const result = geoData.results[0];
                              const gLat = result.geometry.location.lat;
                              const gLng = result.geometry.location.lng;
                              // Extract city from address components
                              // Explicitly exclude sublocality/neighborhood (districts) — only use city-level types
                              let gCity = cityFromCode;
                              const cityTypes = ["locality", "administrative_area_level_2", "administrative_area_level_1"];
                              for (const type of cityTypes) {
                                const comp = result.address_components.find(
                                  (c) => c.types.includes(type) && !c.types.includes("sublocality") && !c.types.includes("neighborhood")
                                );
                                if (comp) { gCity = comp.long_name; break; }
                              }
                              // Use Google's full formatted address (strip the short code prefix if present)
                              const rawFormatted = result.formatted_address || "";
                              // Google sometimes prepends the short code as a premise — use the full formatted address directly
                              const gAddress = rawFormatted || `${val}, ${gCity || "Saudi Arabia"}`;
                              setPropertyData((prev) => ({ ...prev, shortAddress: val, address: gAddress, city: gCity }));
                              setMapCoords({ lat: gLat, lng: gLng });
                            } else if (cityFromCode) {
                              setPropertyData((prev) => ({ ...prev, shortAddress: val, address: `${val}, ${cityFromCode}, Saudi Arabia`, city: cityFromCode }));
                            }
                          } catch (err) {
                            if (cityFromCode) {
                              setPropertyData((prev) => ({ ...prev, shortAddress: val, address: `${val}, ${cityFromCode}, Saudi Arabia`, city: cityFromCode }));
                            }
                          }
                        }
                      }}
                      onFocus={e => (e.target.style.borderColor = "#0e3725")}
                      onBlur={e => (e.target.style.borderColor = "#e2e0d8")}
                    />
                    {shortAddress && shortAddress.length === 8 && (
                      <div style={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)" }}>
                        <svg style={{ width: "14px", height: "14px", color: "#0e3725" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                      </div>
                    )}
                  </div>
                  <p style={{ fontSize: "10px", color: "#9a9a8e", margin: "4px 0 0", lineHeight: 1.3 }}>Saudi Post 8-char code</p>
                </div>

                {/* Full Address - fills remaining space on the right */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <label style={{ display: "block", fontSize: "11px", fontWeight: "700", color: "#4a4a42", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Full Address
                    <span style={{ marginLeft: "8px", fontSize: "10px", color: "#c9a84c", fontWeight: "500", textTransform: "none", letterSpacing: 0 }}>Auto-filled or type to search</span>
                  </label>
                  <div style={{ position: "relative" }}>
                    <svg style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", width: "15px", height: "15px", color: "#9a9a8e", zIndex: 1 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                    <input
                      style={{ width: "100%", padding: "11px 14px 11px 36px", border: "1.5px solid #e2e0d8", borderRadius: "10px", fontSize: "14px", color: "#1a1a14", background: "#fafaf8", outline: "none", boxSizing: "border-box" }}
                      type="text" name="address" value={address} placeholder="Type to search, or enter short address above..."
                      onChange={(e) => { handleChangeInput(e); searchAddress(e.target.value); }}
                      onFocus={e => (e.target.style.borderColor = "#0e3725")}
                      onBlur={e => { e.target.style.borderColor = "#e2e0d8"; setTimeout(() => setShowSuggestions(false), 200); }}
                      autoComplete="off"
                    />
                    {showSuggestions && suggestions.length > 0 && (
                      <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1.5px solid #e2e0d8", borderRadius: "10px", boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 100, marginTop: "4px", overflow: "hidden" }}>
                        {suggestions.map((item, i) => (
                          <div key={i} onMouseDown={() => selectSuggestion(item)} style={{ padding: "10px 14px", fontSize: "13px", color: "#1a1a14", cursor: "pointer", borderBottom: i < suggestions.length - 1 ? "1px solid #f0efe8" : "none" }}
                            onMouseEnter={e => (e.target.style.background = "#f5f4f0")}
                            onMouseLeave={e => (e.target.style.background = "#fff")}>
                            {item.display_name}
                          </div>
                        ))}
                      </div>
                    )}
                    {errors.address && <span style={{ fontSize: "11px", color: "#e05252", marginTop: "4px", display: "block" }}>{errors.address}</span>}
                  </div>
                </div>
              </div>

              {/* Map — draggable pin with reverse geocoding */}
              <DraggableMap
                lat={mapCoords.lat}
                lng={mapCoords.lng}
                address={address}
                onLocationChange={({ lat, lng, address: newAddr, city: newCity }) => {
                  setMapCoords({ lat, lng });
                  setPropertyData((prev) => ({ ...prev, address: newAddr, city: newCity || prev.city }));
                }}
                height="280px"
              />

              {/* City auto-filled - below map */}
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#4a4a42", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                  City
                  <span style={{ marginLeft: "8px", fontSize: "10px", color: "#c9a84c", fontWeight: "500", textTransform: "none", letterSpacing: 0 }}>Auto-filled</span>
                </label>
                <div style={{ width: "100%", padding: "11px 14px", border: "1.5px solid #e2e0d8", borderRadius: "10px", fontSize: "14px", color: city ? "#1a1a14" : "#9a9a8e", background: "#f0efe8", boxSizing: "border-box", display: "flex", alignItems: "center", gap: "8px" }}>
                  <svg style={{ width: "14px", height: "14px", color: city ? "#0e3725" : "#ccc", flexShrink: 0 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                  {city || "Will be detected from the selected location"}
                </div>
              </div>

            </div>
          </div>

          {/* ── Classification ── */}
          <div style={{ background: "#fff", borderRadius: "16px", padding: "32px", marginBottom: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)" }}>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#0e3725", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "20px", paddingBottom: "10px", borderBottom: "1px solid #e8e6e0", display: "flex", alignItems: "center", gap: "8px" }}>
              <svg style={{ width: "18px", height: "18px", color: "#c9a84c" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 6h16M4 10h16M4 14h16M4 18h16"/></svg>
              Classification
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              {/* Sector pills */}
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#4a4a42", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.4px" }}>Sector</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                  {SECTORS.map((sec) => (
                    <div
                      key={sec}
                      onClick={() => setPropertyData((p) => ({ ...p, sector: sec }))}
                      style={{
                        padding: "8px 18px",
                        borderRadius: "50px",
                        fontSize: "13px",
                        fontWeight: "600",
                        cursor: "pointer",
                        border: sector === sec ? "2px solid #0e3725" : "2px solid #e2e0d8",
                        background: sector === sec ? "#0e3725" : "#fff",
                        color: sector === sec ? "#c9a84c" : "#4a4a42",
                        transition: "all 0.18s",
                        userSelect: "none",
                      }}
                    >
                      {sec}
                    </div>
                  ))}
                </div>
                {errors.sector && <span style={{ fontSize: "11px", color: "#e05252", marginTop: "4px", display: "block" }}>{errors.sector}</span>}
              </div>

              {/* Income generating toggle */}
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#4a4a42", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.4px" }}>Income Generating</label>
                <div
                  onClick={() => setPropertyData((p) => ({ ...p, generatic_income: isIncome ? "0" : "1" }))}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "14px",
                    padding: "14px 18px",
                    border: isIncome ? "2px solid #0e3725" : "2px solid #e2e0d8",
                    borderRadius: "12px",
                    cursor: "pointer",
                    background: isIncome ? "#f0f7f4" : "#fafaf8",
                    transition: "all 0.18s",
                    userSelect: "none",
                  }}
                >
                  <div style={{ width: "20px", height: "20px", borderRadius: "6px", border: isIncome ? "2px solid #0e3725" : "2px solid #ccc", background: isIncome ? "#0e3725" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.18s" }}>
                    {isIncome && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#c9a84c" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                  </div>
                  <div>
                    <div style={{ fontSize: "14px", fontWeight: "600", color: "#1a1a14" }}>This property generates rental income</div>
                    <div style={{ fontSize: "12px", color: "#7a7a6e", marginTop: "2px" }}>Check if investors will receive periodic rental distributions</div>
                  </div>
                </div>
                {errors.generatic_income && <span style={{ fontSize: "11px", color: "#e05252", marginTop: "4px", display: "block" }}>{errors.generatic_income}</span>}
              </div>
            </div>
          </div>

          {/* ── Financial Details ── */}
          <div style={{ background: "#fff", borderRadius: "16px", padding: "32px", marginBottom: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)" }}>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#0e3725", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "20px", paddingBottom: "10px", borderBottom: "1px solid #e8e6e0", display: "flex", alignItems: "center", gap: "8px" }}>
              <svg style={{ width: "18px", height: "18px", color: "#c9a84c" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
              Financial Details
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#4a4a42", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.4px" }}>Total Estimated Value (<RiyalSymbol size="0.9em" />SAR)</label>
                <input style={{ width: "100%", padding: "11px 14px", border: "1.5px solid #e2e0d8", borderRadius: "10px", fontSize: "14px", color: "#1a1a14", background: "#fafaf8", outline: "none", boxSizing: "border-box" }} type="text" inputMode="numeric" name="propertyEstimatedValue" value={propertyEstimatedValue ? Number(propertyEstimatedValue.replace(/,/g, "")).toLocaleString("en-US") : ""} placeholder="e.g. 8,500,000" onChange={(e) => { const raw = e.target.value.replace(/,/g, ""); if (/^\d*$/.test(raw)) setPropertyData((prev) => ({ ...prev, propertyEstimatedValue: raw })); }} onFocus={e => (e.target.style.borderColor = "#0e3725")} onBlur={e => (e.target.style.borderColor = "#e2e0d8")} />
                {errors.propertyEstimatedValue && <span style={{ fontSize: "11px", color: "#e05252", marginTop: "4px", display: "block" }}>{errors.propertyEstimatedValue}</span>}
              </div>
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#4a4a42", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.4px" }}>Ownership Offered (%)</label>
                <input style={{ width: "100%", padding: "11px 14px", border: "1.5px solid #e2e0d8", borderRadius: "10px", fontSize: "14px", color: "#1a1a14", background: "#fafaf8", outline: "none", boxSizing: "border-box" }} type="number" name="percentageOfOwnership" value={percentageOfOwnership} placeholder="e.g. 40" min="1" max="100" onChange={handleChangeInput} onFocus={e => (e.target.style.borderColor = "#0e3725")} onBlur={e => (e.target.style.borderColor = "#e2e0d8")} />
                {errors.percentageOfOwnership && <span style={{ fontSize: "11px", color: "#e05252", marginTop: "4px", display: "block" }}>{errors.percentageOfOwnership}</span>}
              </div>
            </div>
          </div>

          {/* ── Photos & Documents ── */}
          <div style={{ background: "#fff", borderRadius: "16px", padding: "32px", marginBottom: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)" }}>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#0e3725", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "20px", paddingBottom: "10px", borderBottom: "1px solid #e8e6e0", display: "flex", alignItems: "center", gap: "8px" }}>
              <svg style={{ width: "18px", height: "18px", color: "#c9a84c" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              Photos &amp; Documents
            </div>

            {/* Thumbnails */}
            {images.files.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "12px" }}>
                {Object.entries(images.thumbnails).map(([name, src]) => (
                  <div key={name} style={{ position: "relative", width: "90px", height: "90px", borderRadius: "10px", overflow: "hidden", border: "1.5px solid #e2e0d8" }}>
                    <img src={src} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    <button type="button" onClick={() => removeImage(name)} style={{ position: "absolute", top: "4px", right: "4px", background: "rgba(0,0,0,0.55)", color: "#fff", border: "none", borderRadius: "50%", width: "20px", height: "20px", fontSize: "13px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>&#215;</button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload zone */}
            <label htmlFor="image-input" style={{ border: "2px dashed #d4d2c8", borderRadius: "12px", padding: "24px", textAlign: "center", cursor: "pointer", background: "#fafaf8", display: "block" }}>
              <svg style={{ width: "32px", height: "32px", color: "#c9a84c", margin: "0 auto", display: "block" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              <p style={{ fontSize: "13px", color: "#7a7a6e", marginTop: "8px", marginBottom: 0 }}>
                <strong style={{ color: "#0e3725" }}>Click to upload photos</strong> or drag and drop<br />
                <span style={{ fontSize: "11px" }}>JPG, PNG, WebP up to 1MB each — these will appear on the property listing page</span>
              </p>
            </label>
            <input id="image-input" type="file" name="imageName" accept="image/jpeg,image/png,image/webp,image/jpg" multiple style={{ display: "none" }} onChange={(e) => handleChangeFile(e.target.files, "images")} onClick={(e) => (e.target.value = null)} />
            {errors.imageName && <span style={{ fontSize: "11px", color: "#e05252", marginTop: "4px", display: "block" }}>{errors.imageName}</span>}

            {/* Documents upload */}
            <div style={{ marginTop: "20px" }}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#4a4a42", marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                Supporting Documents
                <span style={{ marginLeft: "8px", fontSize: "10px", color: "#9a9a8e", fontWeight: "400", textTransform: "none", letterSpacing: 0 }}>Property deed, title, permits, valuation reports, etc.</span>
              </label>
              {/* Uploaded docs list */}
              {docs.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "10px" }}>
                  {docs.map((doc, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "9px 12px", border: "1.5px solid #e2e0d8", borderRadius: "8px", background: "#f5f4f0" }}>
                      <svg style={{ width: "15px", height: "15px", color: "#0e3725", flexShrink: 0 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      <span style={{ fontSize: "13px", color: "#0e3725", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.name}</span>
                      <button type="button" onClick={() => setDocs(docs.filter((_, j) => j !== i))} style={{ background: "none", border: "none", cursor: "pointer", color: "#9a9a8e", fontSize: "16px", lineHeight: 1, padding: "0 2px" }}>&#215;</button>
                    </div>
                  ))}
                </div>
              )}
              {/* Upload zone */}
              <label htmlFor="docs-input" style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px 16px", border: "2px dashed #d4d2c8", borderRadius: "10px", background: "#fafaf8", cursor: "pointer" }}>
                <svg style={{ width: "18px", height: "18px", color: "#c9a84c", flexShrink: 0 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                <span style={{ fontSize: "13px", color: "#7a7a6e" }}>
                  <strong style={{ color: "#0e3725" }}>Click to add documents</strong>
                  <span style={{ fontSize: "11px", display: "block", marginTop: "2px" }}>PDF, Word, Excel — multiple files allowed</span>
                </span>
              </label>
              <input id="docs-input" type="file" accept=".pdf,.doc,.docx,.xls,.xlsx" multiple style={{ display: "none" }}
                onChange={(e) => {
                  const newFiles = Array.from(e.target.files);
                  setDocs((prev) => {
                    const existingNames = new Set(prev.map(f => f.name));
                    const unique = newFiles.filter(f => !existingNames.has(f.name));
                    const updated = [...prev, ...unique];
                    setPropertyData((p) => ({ ...p, propertyDeed: updated[0]?.name || "" }));
                    return updated;
                  });
                  e.target.value = null;
                }}
              />
              {errors.propertyDeed && <span style={{ fontSize: "11px", color: "#e05252", marginTop: "4px", display: "block" }}>{errors.propertyDeed}</span>}
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            style={{ width: "100%", padding: "15px", background: "linear-gradient(135deg, #c9a84c 0%, #e0bc55 100%)", color: "#fff", border: "none", borderRadius: "12px", fontSize: "15px", fontWeight: "700", cursor: "pointer", letterSpacing: "0.3px", boxShadow: "0 4px 20px rgba(201,168,76,0.45)", transition: "transform 0.15s, box-shadow 0.15s" }}
            onMouseEnter={e => { e.target.style.transform = "translateY(-1px)"; e.target.style.boxShadow = "0 6px 24px rgba(201,168,76,0.55)"; }}
            onMouseLeave={e => { e.target.style.transform = ""; e.target.style.boxShadow = "0 4px 20px rgba(201,168,76,0.45)"; }}
          >
            Submit Property for Review
          </button>

        </form>
      </div>
    </div>
  );
};

export default CreateProperty;
